import { startsWith } from 'lodash/fp';
import Cookies from 'js-cookie';
import config from '../helpers/config';

// This class is to unify the PostMeasureOptions cookie.
// It seeks to nullify two issues with the original definition
// of the cookie.
//
// 1) The cookie is NOT Url encoded. This is problematic since
//    most cookie libraries require url encoding (a good thing)
//    and since if for any reason, there is a semicolon in the
//    JSON string, this cookie will break and be unusable.
//    Instead of having to a) know the cookie is malformed (not
//    url encoded) and read it improperly in a bunch of places,
//    and b) when we DO fix this have to go through looking for
//    places it is read/written, just use this class and we can
//    easily update it to url encode properly later, and your
//    code will magically work!
//
// 2) In old autobahn code, we use PascalCase for property names
//    for some unknown reason. To ensure compatibility while
//    keeping with JavaScript norms, please use this class and
//    access properties using the standard, accepted style of
//    camelCase. This class will handle the conversion to
//    PascalCase for you, and ensure compatibility with legacy
//    code while allowing your code to be correct! Eventually,
//    if/when we finally update/remove the legacy code, we can
//    drop the PascalCase conversion and everything will work
//    just fine, no code changes necessary

export default class PostMeasureOptionsCookie {
  static get isPresent() {
    return Cookies.get(PostMeasureOptionsCookie.COOKIE_NAME);
  }

  constructor() {
    // define property accessors on this object with the proper
    // camel case accessors.
    Object.defineProperties(
      this,
      Object.keys(PostMeasureOptionsCookie.PROPERTIES).reduce((memo, key) => {
        const camelCasePropName = key.charAt(0).toLowerCase() + key.slice(1);

        const accessors = {
          // all props should be readable
          get() {
            return this[key];
          },
        };

        if (PostMeasureOptionsCookie.PROPERTIES[key].indexOf('set') !== -1) {
          accessors.set = (value) => {
            this[key] = value;
          };
        } else {
          accessors.set = () => {
            throw new Error(`postMeasureOptions.${camelCasePropName} is read only`);
          };
        }

        const reduced = { ...memo };
        reduced[camelCasePropName] = accessors;

        return reduced;
      }, {}),
    );

    const rawCookie = Cookies.get(PostMeasureOptionsCookie.COOKIE_NAME);
    if (rawCookie) {
      const cookie = startsWith('%', rawCookie)
        ? decodeURIComponent(rawCookie)
        : rawCookie;

      const json = JSON.parse(cookie);
      Object.assign(this, json);
    }
  }

  save() {
    const json = JSON.stringify(this);
    Cookies.set(PostMeasureOptionsCookie.COOKIE_NAME, json, {
      domain: config.cookieDomainPostMeasureOptions, expires: 1, priority: 'high', path: '/',
    });
  }

  delete() {
    Cookies.remove(PostMeasureOptionsCookie.COOKIE_NAME, { domain: config.cookieDomainPostMeasureOptions, path: '/' });
    Object.keys(this).forEach((key) => { this[key] = undefined; });
  }
}

PostMeasureOptionsCookie.COOKIE_NAME = 'postMeasureOptions';
PostMeasureOptionsCookie.PROPERTIES = {
  Height: ['get', 'set'],
  Width: ['get', 'set'],
  Depth: ['get', 'set'],
  HeightWhole: ['get', 'set'],
  WidthWhole: ['get', 'set'],
  HeightEighth: ['get', 'set'],
  WidthEighth: ['get', 'set'],
  WindowName: ['get', 'set'],
  RoomName: ['get', 'set'],
  IsCutout: ['get', 'set'],
  IsPostMeasureMode: ['get', 'set'],
  IsSpecialtyShape: ['get', 'set'],
  Tags: ['get', 'set'],
  PropertyName: ['get', 'set'],
  PropertyAddress: ['get', 'set'],
  WindowDescription: ['get', 'set'],
  MeasurementDate: ['get', 'set'],
  JobUid: ['get', 'set'],
  QuoteUid: ['get', 'set'],
  WindowUid: ['get', 'set'],
  BaseUrl: ['get', 'set'],
  RedirectUrl: ['get', 'set'],
  RoomLabel: ['get', 'set'],
  HasIREndpoint: ['get', 'set'],
  PlpFilters: ['get', 'set'],
  PurchaseOrderNumber: ['get', 'set'],
  LineItemNumber: ['get', 'set'],
  OriginalProductId: ['get', 'set'],
  Destination: ['get', 'set'],
  IsRemake: ['get', 'set'],
  ScrollOnReturn: ['get', 'set'],
  // read only
  InterconnectedRetailEndpoint: ['get'],
};
